/**
 * Short, quiet one-shot sound effects, each rate-limited so busy moments
 * (a swarm dying, a wave of income ticks) never turn into a machine gun.
 */

import { ensureGraph } from './context';
import { noteToFreq } from './notes';
import { playNoise, playTone } from './synth';
import { debugCounters } from './music';

const lastPlayedAt = new Map<string, number>();

function allowed(key: string, minIntervalMs: number): boolean {
  const now = Date.now();
  const last = lastPlayedAt.get(key) ?? -Infinity;
  if (now - last < minIntervalMs) return false;
  lastPlayedAt.set(key, now);
  return true;
}

function tone(note: string, dur: number, shape: 'square' | 'triangle', gain: number, duty?: number, delay = 0): void {
  const { ctx, sfxBus } = ensureGraph();
  const freq = noteToFreq(note);
  if (freq === null) return;
  playTone(ctx, sfxBus, ctx.currentTime + delay, freq, dur, shape, { gain, duty });
  debugCounters.notesScheduled++;
}

/** Soft UI tap — used for every ordinary button/card click. Lightly
 * rate-limited so a rapid double-fire (touch + synthetic click) can't
 * double up audibly. */
export function playUiTap(): void {
  if (!allowed('ui-tap', 60)) return;
  tone('E5', 0.045, 'square', 0.05, 0.2);
}

/** Low buzz for a click that didn't do anything useful (a miss inside a
 * button grid's gutter, or a click while no valid target exists). */
export function playInvalidBuzz(): void {
  if (!allowed('invalid', 250)) return;
  tone('A2', 0.09, 'square', 0.07, 0.5);
}

/** Two-note chirp: a unit finished training or a building finished
 * construction. */
export function playBuildComplete(): void {
  if (!allowed('build-complete', 200)) return;
  tone('C5', 0.07, 'square', 0.07, 0.3, 0);
  tone('G5', 0.09, 'square', 0.07, 0.3, 0.07);
}

/** Three-note rising chirp: research completed. */
export function playResearchComplete(): void {
  if (!allowed('research-complete', 300)) return;
  tone('C5', 0.07, 'square', 0.07, 0.25, 0);
  tone('E5', 0.07, 'square', 0.07, 0.25, 0.08);
  tone('A5', 0.11, 'square', 0.08, 0.25, 0.16);
}

/** Very quiet coin tick for serve income — globally rate-limited to at most
 * once every 1.5s regardless of how many agents are serving at once. */
export function playServeIncome(): void {
  if (!allowed('serve-income', 1500)) return;
  tone('B5', 0.045, 'square', 0.035, 0.15);
}

/** Two-tone warning: under attack. Max once every 5s. */
export function playUnderAttackAlert(): void {
  if (!allowed('under-attack', 5000)) return;
  tone('A4', 0.09, 'square', 0.09, 0.4, 0);
  tone('D4', 0.14, 'square', 0.09, 0.4, 0.1);
}

/** Distinctive wobble for a cost anomaly alarm. Max once every 3s. */
export function playAnomalyAlarm(): void {
  if (!allowed('anomaly', 3000)) return;
  const { ctx, sfxBus } = ensureGraph();
  const base = noteToFreq('F#4') ?? 370;
  const osc = ctx.createOscillator();
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  const env = ctx.createGain();
  osc.type = 'square';
  osc.frequency.value = base;
  lfo.frequency.value = 11; // wobble rate
  lfoGain.gain.value = 40; // wobble depth in Hz
  lfo.connect(lfoGain);
  lfoGain.connect(osc.frequency);
  osc.connect(env);
  env.connect(sfxBus);
  const t = ctx.currentTime;
  const dur = 0.4;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(0.08, t + 0.02);
  env.gain.setValueAtTime(0.08, t + dur - 0.08);
  env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  lfo.start(t);
  osc.start(t);
  lfo.stop(t + dur + 0.02);
  osc.stop(t + dur + 0.02);
  debugCounters.notesScheduled++;
}

/** Short noise burst: a unit died. */
export function playUnitDeath(): void {
  if (!allowed('unit-death', 180)) return;
  const { ctx, sfxBus } = ensureGraph();
  playNoise(ctx, sfxBus, ctx.currentTime, 0.12, { gain: 0.07, filterHz: 2200, filterType: 'bandpass', release: 0.09 });
  debugCounters.notesScheduled++;
}

/** Deeper noise thud: a building was destroyed. */
export function playBuildingDestroyed(): void {
  if (!allowed('building-destroyed', 350)) return;
  const { ctx, sfxBus } = ensureGraph();
  playNoise(ctx, sfxBus, ctx.currentTime, 0.32, { gain: 0.1, filterHz: 500, filterType: 'lowpass', release: 0.28 });
  debugCounters.notesScheduled++;
}
